import { Directive, ElementRef, HostListener, Input, Renderer2, OnInit, OnDestroy } from '@angular/core';
import { ScreensService } from '../services/screens.service';
import { ReAutheticateComponent } from 'src/app/user/re-autheticate/re-autheticate.component';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { PasswordConfigurationPopUpComponent } from 'src/app/user/password-configuration/password-configuration-pop-up/password-configuration-pop-up.component';
import { ScreenPermissionServiceService } from '../services/screen-permission-service.service';
import { DigitalSignatureComponent } from 'src/app/user/digital-signature/digital-signature.component';
import { eventNames } from 'process';

@Directive({
    selector: '[appCriticalOperation]',
    standalone: false
})
export class CriticalOperationDirective implements OnInit, OnDestroy {

  @Input('appCriticalOperation') buttonId!: number;
  @Input() criticalMethod!: (event?: Event) => void;

  private isButtonEnabled = false;
  private criticalCode: string | null = null;

  private permissionSubscription!: Subscription;
  private criticalSubscription!: Subscription;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private screensService: ScreenPermissionServiceService,
    
    private dialog: MatDialog
  ) {}

ngOnInit(): void {
   /* ===== BYPASS RULE FOR BUTTON 1111 ===== */
  if (this.buttonId === 1111) {
    this.isButtonEnabled = true;
    this.criticalCode = null;

    this.renderer.removeAttribute(this.el.nativeElement, 'disabled');
    console.log('Button 1111 bypassed: always enabled & non-critical');
    return; 
  }
  if (this.buttonId === undefined || this.buttonId === null) {
    this.renderer.setAttribute(this.el.nativeElement, 'disabled', 'true');
    console.warn('appCriticalOperation requires a buttonId');
    return;
  }

  // --- Immediate check for current permission ---
  this.isButtonEnabled = this.screensService.has(this.buttonId);
  if (this.isButtonEnabled) {
    this.renderer.removeAttribute(this.el.nativeElement, 'disabled');
  } else {
    this.renderer.setAttribute(this.el.nativeElement, 'disabled', 'true');
  }

  // --- Subscribe to future changes ---
  this.permissionSubscription = this.screensService.changes().subscribe(allowedIds => {
    this.isButtonEnabled = allowedIds.has(this.buttonId);
    if (this.isButtonEnabled) {
      this.renderer.removeAttribute(this.el.nativeElement, 'disabled');
    } else {
      this.renderer.setAttribute(this.el.nativeElement, 'disabled', 'true');
    }
    console.log(`Button ${this.buttonId} enabled: ${this.isButtonEnabled}`);
  });

  // --- Critical code subscription ---
  this.criticalSubscription = this.screensService.getCriticalButtons().subscribe(map => {
    this.criticalCode = map.get(this.buttonId) ?? null;
    console.log(`Button ${this.buttonId} → criticalCode =`, this.criticalCode);
  });
}


  @HostListener('click', ['$event'])
  handleClick(event: Event): void {

    /* ---------- Block disabled buttons ---------- */
    if (!this.isButtonEnabled || this.el.nativeElement.hasAttribute('disabled')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    /* ---------- Non-critical ---------- */
    if (!this.criticalCode) {
      this.criticalMethod?.(event);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    /* ---------- Decide popup by lookup code ---------- */
    switch (this.criticalCode) {

      case '1': // Re-authentication
        this.openReAuthPopup();
        break;

      case '2': // Digital Signature
        this.openDigitalSignaturePopup();
        break;

      case '3': // Future critical type
        console.warn('Critical type 3 not implemented yet');
         this.criticalMethod?.(event); 
        break;

      default:
        console.warn('Unknown critical code:', this.criticalCode);
        this.criticalMethod?.(event);
    }
  }

  /* ================= POPUPS ================= */

  private openReAuthPopup(): void {
    const dialogRef = this.dialog.open(ReAutheticateComponent, {
      width: '33vw',
      maxWidth: '33vw',
      disableClose: true,
      data: {
        buttonId: this.buttonId,
        buttonName: this.el.nativeElement.title || `Button ${this.buttonId}`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.data) {
        this.criticalMethod?.(event);
      }
    });
  }

  private openDigitalSignaturePopup(): void {
    const dialogRef = this.dialog.open(DigitalSignatureComponent, {
      width: '40vw',
      disableClose: true,
      data: {
        buttonId: this.buttonId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.data) {
        this.criticalMethod?.(event);
      }
    });
  }

  ngOnDestroy(): void {
    this.permissionSubscription?.unsubscribe();
    this.criticalSubscription?.unsubscribe();
  }
}
